import React from 'react';
import { motion } from 'framer-motion';
import '../styles/main.css';
import heroVideo from '../assets/hero.mp4';
import chicken from '../assets/chicken.jpg';
import mutton from '../assets/mutton.jpg';
import { FaCrown, FaShippingFast, FaHandsWash, FaRegStar } from 'react-icons/fa';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};
const itemFade = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
};

const signatureDishes = [
  {
    img: chicken,
    title: "Chicken Dum Biryani",
    desc: "Slow-cooked with authentic spices.",
    rating: 4.9,
    chefNote: "A royal classic, slow-cooked to perfection!"
  },
  {
    img: mutton,
    title: "Mutton Dum Biryani",
    desc: "Tender, juicy and full of flavor.",
    rating: 4.8,
    chefNote: "Our chef’s favorite, rich and aromatic."
  }
];

const features = [
  {
    icon: <FaCrown size={40} color="#ffd700" />,
    title: 'Royal Recipes',
    desc: 'Authentic dum biryani passed down generations.',
  },
  {
    icon: <FaHandsWash size={40} color="#ffd700" />,
    title: 'Hygienic',
    desc: 'Hygienically cooked and sealed to perfection.',
  },
  {
    icon: <FaShippingFast size={40} color="#ffd700" />,
    title: 'Fast Delivery',
    desc: 'Lightning fast delivery at your doorstep.',
  },
];

export default function Home() {
  return (
    <div className="royal-background premium-bg">
      {/* Premium Glow Banner */}
      <motion.div
        className="limited-offer-banner premium-glow"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        style={{
          fontWeight: 600,
          fontSize: '1.2rem',
          letterSpacing: '0.04em',
          background: 'linear-gradient(90deg, #ffd700cc 60%, #fffbe7 100%)',
          color: '#001d4a',
          borderRadius: '2rem',
          boxShadow: '0 2px 24px #ffd70055',
          padding: '0.7rem 2.2rem',
          margin: '2.5rem auto 1.5rem',
          maxWidth: 480,
        }}
      >
        <span role="img" aria-label="clock">⏰</span>
        &nbsp;Order before <strong>7 PM</strong> and enjoy a <span style={{ color: '#b8860b' }}>free royal dessert!</span>
      </motion.div>

      {/* Hero Section */}
      <section
        className="hero-container hero-section"
        style={{
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <video
          className="hero-video hero-bg-video"
          autoPlay
          loop
          muted
          playsInline
          style={{
            filter: 'brightness(0.7) blur(1.5px)',
            borderRadius: '2.5rem',
            boxShadow: '0 8px 48px #ffd70044'
          }}
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div
          className="hero-content"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0,29,74,0.72)',
            borderRadius: '2rem',
            padding: '2.5rem 3rem',
            boxShadow: '0 4px 32px #ffd70033',
            textAlign: 'center',
            width: '90%',
            maxWidth: 600
          }}
        >
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            style={{
              fontSize: '2.7rem',
              fontWeight: 800,
              letterSpacing: '0.04em',
              color: '#ffd700',
              textShadow: '0 2px 24px #000a'
            }}
          >
            Welcome to <span style={{ color: '#fffbe7', textShadow: '0 2px 24px #ffd70099' }}>Hungry King</span>
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            style={{
              fontSize: '1.3rem',
              color: '#fffbe7',
              margin: '1.2rem 0 2rem',
              textShadow: '0 1px 8px #001d4a99'
            }}
          >
            Experience the <span style={{ color: '#ffd700', fontWeight: 600 }}>royal</span> taste of authentic biryani, crafted for true connoisseurs.
          </motion.p>
          <motion.a
            href="/menu"
            whileHover={{ scale: 1.09, boxShadow: '0 0 32px #ffd700cc' }}
            whileTap={{ scale: 0.97 }}
          >
            <button
              className="primary-btn hero-btn"
              style={{
                fontSize: '1.2rem',
                padding: '0.9rem 2.5rem',
                borderRadius: '2rem',
                background: 'linear-gradient(90deg, #ffd700 60%, #fffbe7 100%)',
                color: '#001d4a',
                fontWeight: 700,
                boxShadow: '0 2px 24px #ffd70055'
              }}
            >
              Explore Menu
            </button>
          </motion.a>
        </div>
      </section>

      {/* Signature Dishes */}
      <motion.section
        className="featured-section section-wrapper dishes-section"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        style={{ marginTop: '3rem' }}
      >
        <h2
          className="section-title"
          style={{
            color: '#ffd700',
            fontWeight: 700,
            fontSize: '2rem',
            letterSpacing: '0.03em',
            textAlign: 'center'
          }}
        >
          Our Signature Dishes
        </h2>
        <div
          className="dishes-grid"
          style={{
            display: 'flex',
            gap: '2.5rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: '2rem'
          }}
        >
          {signatureDishes.map((dish, idx) => (
            <motion.article
              className="dish-card"
              key={dish.title}
              variants={itemFade}
              whileHover={{
                scale: 1.09,
                boxShadow: '0 8px 32px #ffd70077, 0 2px 16px #fff4'
              }}
              tabIndex={0}
              style={{
                background: 'linear-gradient(135deg, #fffbe7 70%, #ffd70022 100%)',
                borderRadius: '1.5rem',
                boxShadow: '0 2px 18px #ffd70033',
                padding: '2rem 1.5rem',
                minWidth: 260,
                maxWidth: 320,
                textAlign: 'center',
                position: 'relative'
              }}
            >
              <img
                src={dish.img}
                alt={dish.title}
                className="dish-img"
                loading="lazy"
                style={{
                  width: '100%',
                  height: '160px',
                  objectFit: 'cover',
                  borderRadius: '1.2rem',
                  boxShadow: '0 2px 12px #ffd70022'
                }}
              />
              <h3
                style={{
                  color: '#001d4a',
                  fontWeight: 700,
                  margin: '1rem 0 0.5rem'
                }}
              >
                {dish.title}
              </h3>
              <p style={{ color: '#444', fontWeight: 500 }}>{dish.desc}</p>
              <div
                className="dish-rating"
                style={{
                  margin: '0.7rem 0',
                  fontWeight: 600,
                  color: '#b8860b'
                }}
              >
                <FaRegStar color="#ffd700" /> {dish.rating}
              </div>
              <motion.div
                className="chef-note"
                whileHover={{ scale: 1.05, color: '#ffd700' }}
                style={{
                  fontStyle: 'italic',
                  color: '#001d4a',
                  fontWeight: 500,
                  marginTop: '0.7rem'
                }}
              >
                <span role="img" aria-label="chef">👨‍🍳</span> {dish.chefNote}
              </motion.div>
            </motion.article>
          ))}
        </div>
      </motion.section>

      {/* Why Choose Us */}
      <motion.section
        className="trust-section section-wrapper why-choose-section"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        style={{
          marginTop: '3.5rem',
          background: 'rgba(0,29,74,0.93)',
          border: '2.5px solid #ffd700',
          borderRadius: '18px',
          boxShadow: '0 4px 24px rgba(212,175,55,0.13)',
          padding: '2.2rem 1.2rem 2.5rem 1.2rem',
        }}
      >
        <h2 className="section-title">Why Choose Hungry King?</h2>
        <p className="section-subtitle">
          Experience the royal difference—here’s what sets us apart.
        </p>
        <div className="features-grid">
          {features.map((feature, idx) => (
            <motion.article
              className="feature-card"
              key={feature.title + idx}
              variants={itemFade}
              tabIndex={0}
              whileHover={{ scale: 1.07, background: '#fffbe7' }}
            >
              <span className="feature-icon" aria-label={feature.title + " icon"}>
                {feature.icon}
              </span>
              <h4>{feature.title}</h4>
              <p>{feature.desc}</p>
            </motion.article>
          ))}
        </div>
        {/* Chef Quote */}
        <div className="chef-quote" style={{
          margin: '3rem auto 1.5rem',
          maxWidth: 480,
          background: 'linear-gradient(90deg, #ffd70033 60%, #fffbe7 100%)',
          borderRadius: '1.5rem',
          padding: '1.5rem 2rem',
          boxShadow: '0 2px 12px #ffd70022',
          display: 'flex',
          alignItems: 'center',
          gap: '1.2rem'
        }}>
          <img src="/assets/chef.png" alt="Chef" className="chef-photo"
            style={{
              width: 70,
              height: 70,
              borderRadius: '50%',
              objectFit: 'cover',
              border: '3px solid #ffd700',
              boxShadow: '0 2px 12px #ffd70044'
            }} />
          <blockquote style={{ margin: 0, color: '#001d4a', fontWeight: 600, fontStyle: 'italic' }}>
            <span>“Every grain of rice is a promise of royal taste.”</span>
            <footer style={{ fontWeight: 400, color: '#b8860b', marginTop: '0.5rem' }}>— Chef Arjun, Head Chef</footer>
          </blockquote>
        </div>
        {/* Delivery Partners */}
        <section className="partners-carousel" style={{ marginTop: '2rem', textAlign: 'center' }}>
          <h3 className="partners-title" style={{
            color: '#ffd700',
            fontWeight: 700,
            fontSize: '1.1rem',
            marginBottom: '1rem'
          }}>Trusted Delivery Partners</h3>
          <div className="partners-logos" style={{
            display: 'flex',
            gap: '2rem',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <img src="/assets/zomato.png" alt="Zomato" style={{ height: 36 }} />
            <img src="/assets/swiggy.png" alt="Swiggy" style={{ height: 36 }} />
            <img src="/assets/dunzo.png" alt="Dunzo" style={{ height: 36 }} />
          </div>
        </section>
      </motion.section>

      {/* Testimonials */}
      <motion.section
        className="testimonials section-wrapper testimonials-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        style={{ marginTop: '3.5rem' }}
      >
        <h2 className="section-title">What Our Customers Say</h2>
        <div className="testimonials-grid" style={{
          display: 'flex',
          gap: '2rem',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginTop: '2rem'
        }}>
          <motion.blockquote className="testimonial-card" variants={itemFade} style={{
            background: 'linear-gradient(135deg, #fffbe7 70%, #ffd70022 100%)',
            borderRadius: '1.2rem',
            boxShadow: '0 2px 12px #ffd70022',
            padding: '1.5rem 1.2rem',
            minWidth: 220,
            maxWidth: 320,
            color: '#001d4a',
            fontWeight: 500,
            fontStyle: 'italic'
          }}>
            “Hands down the best biryani I’ve ever had. 5 stars!” — <strong>Raj K.</strong>
          </motion.blockquote>
          <motion.blockquote className="testimonial-card" variants={itemFade} style={{
            background: 'linear-gradient(135deg, #fffbe7 70%, #ffd70022 100%)',
            borderRadius: '1.2rem',
            boxShadow: '0 2px 12px #ffd70022',
            padding: '1.5rem 1.2rem',
            minWidth: 220,
            maxWidth: 320,
            color: '#001d4a',
            fontWeight: 500,
            fontStyle: 'italic'
          }}>
            “Truly a royal experience from the first bite to the last.” — <strong>Anita M.</strong>
          </motion.blockquote>
        </div>
      </motion.section>

      {/* Call To Action */}
      <motion.section
        className="cta-banner section-wrapper cta-section"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        style={{ marginTop: '3.5rem', textAlign: 'center' }}
      >
        <h2 style={{
          color: '#001d4a',
          background: '#ffd700',
          fontWeight: 900,
          fontSize: '2.3rem',
          letterSpacing: '0.05em',
          marginBottom: '1.2rem',
          borderRadius: '1.2rem',
          padding: '0.7rem 1.5rem',
          display: 'inline-block',
          boxShadow: '0 2px 16px #ffd70055'
        }}>
          👑 Experience Royalty in Every Bite!
        </h2>
        <motion.a href="/menu" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
          <button className="primary-btn large" style={{
            fontSize: '1.2rem',
            padding: '1rem 3rem',
            borderRadius: '2rem',
            background: '#ffd700',
            color: '#001d4a',
            fontWeight: 700,
            boxShadow: '0 2px 24px #ffd70055'
          }}>
            Order Now
          </button>
        </motion.a>
      </motion.section>

      {/* WhatsApp Bubble */}
      <a
        href="https://wa.me/919999999999?text=Hi%20Hungry%20King!%20I%20want%20to%20order%20biryani."
        className="whatsapp-bubble"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          zIndex: 1000,
          boxShadow: '0 2px 16px #25d36655',
          borderRadius: '50%',
          background: '#fff',
          padding: '0.5rem'
        }}
      >
        <img src="/assets/whatsapp.svg" alt="WhatsApp" style={{ width: 48, height: 48 }} />
      </a>
    </div>
  );
}
